import React, { useEffect, useState } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Card, Drawer, Dropdown, Menu, message, Popconfirm, Progress, Tag } from "antd";
import { Container, Row, Col } from "react-bootstrap";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { Link } from "react-router-dom";
import { createTaskBoard, deleteTaskBoard, fetchTaskBoards, updateTaskBoard } from "../../../core/data/redux/projects/taskBoard/taskBoardActions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchTasks, updateTask } from "../../../core/data/redux/projects/task/taskActions";
import { formatDate } from "../../../core/common/GlobalFunctions/DateFormats";
import { EllipsisOutlined } from "@ant-design/icons";
import { fetchAllProjects } from "../../../core/data/redux/projects/project/projectAction";
import { filterByStatus } from "../../../core/utils/filterActiveData";
import { decryptData } from "../../../core/utils/encryptUtils";
import { getEmployeeByUserId } from "../../../core/data/redux/employee/employeeAction";

interface ColumnProps {
    id: string;
    column: any;
    moveTask: (id: number, targetColumnId: string) => void;
    taskBoardId: number; // <-- new prop
}

interface Task {
    TaskID: number;
    Title: string;
    DueDate: string;
    Priority: string;
    progress: number;
    assignedEmployees?: { id: number; PictureURL: string }[];
}

interface ColumnType {
    name: string;
    color: string;
    tasks: Task[];
    taskBoardId: number;
}


const TaskTypes = { TASK: "task" };

const TaskComponent: React.FC<{ task: any; moveTask: (id: number, targetColumnId: string) => void }> = ({ task }) => {

    const [{ isDragging }, drag] = useDrag(() => ({
        type: TaskTypes.TASK,
        item: { id: task.TaskID },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }));

    return (
        <Card
            ref={drag}
            className="position-relative"
            style={{
                opacity: isDragging ? 0.5 : 1,
                width: 320,
                borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0,0,0,.06)',
                minHeight: 60,
                marginTop: 10,
            }}
        >
            {/* Header: Priority, Employees & Attachment */}
            <div
                className="d-flex justify-content-between align-items-center mb-1"
                title={task?.Description || "No description available"}
            >
                <div className="d-flex align-items-center">
                    {/* Priority */}
                    <Tag
                        color={
                            task.Priority === "High"
                                ? "red"
                                : task.Priority === "Medium"
                                    ? "yellow"
                                    : "green"
                        }
                        style={{ fontSize: 12, padding: '0 8px', height: 22 }}
                    >
                        {task.Priority}
                    </Tag>

                    {/* Employee Avatars (right of priority) */}

                    <div className="avatar-list-stacked avatar-group-sm  d-flex align-items-center">
                        {task?.assignedEmployees?.map((emp: any) => (
                            <span key={emp.id} className="avatar avatar-rounded" title={emp?.name}>
                                <img
                                    className="border border-white"
                                    src={emp?.PictureURL ?? "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                                    alt={emp?.name}
                                />
                            </span>
                        ))}
                    </div>

                </div>

                {/* Attachment Icon (top-right) */}
                {task.AttachmentPath && (
                    <a
                        href={task.AttachmentPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-center border-0 p-0"
                        title="View Document"
                    >
                        <i
                            className="bi bi-file-earmark-text text-primary"
                            style={{ fontSize: 18 }}
                        ></i>
                    </a>
                )}
            </div>

            {/* Title */}
            <div
                style={{
                    fontWeight: 600,
                    fontSize: 15,
                    lineHeight: 1.2,
                    marginBottom: 6,
                }}
            >
                {task.Title}
            </div>

            {/* Time Info: Due & Hours */}
            <div
                className="d-flex justify-content-between align-items-center mb-1"
                style={{ fontSize: 12 }}
            >
                <span className="text-muted">
                    <i className="bi bi-calendar-event me-1"></i> Due: {formatDate(task.DueDate)}
                </span>
                <span className="text-muted">
                    <i className="bi bi-clock me-1"></i> {task.TotalHours}h
                </span>
            </div>

            {/* Footer: Created & Modified */}
            <div className="d-flex justify-content-between" style={{ fontSize: 11, color: '#678' }}>
                <span>
                    <i className="bi bi-plus-circle me-1"></i> {formatDate(task.CreatedAt)}
                </span>
                <span>
                    <i className="bi bi-pencil me-1"></i> {formatDate(task.ModifiedAt)}
                </span>
            </div>
        </Card>
    );
};

const Column: React.FC<ColumnProps> = ({ id, column, moveTask, taskBoardId }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [, drop] = useDrop(() => ({
        accept: TaskTypes.TASK,
        drop: (item: { id: number }) => moveTask(item.id, id),
    }));

    const handleDeleteBoard = async () => {
        try {
            await dispatch(deleteTaskBoard(Number(taskBoardId)));
            await dispatch(fetchTaskBoards())
            message.success('Task board deleted');
        } catch (err) {
            console.error('Failed to delete task board', err);
        }
    };
    const menu = (
        <Menu>
            <Menu.Item key="delete" disabled={column.tasks.length > 0}>
                <Popconfirm
                    title="Are you sure to delete this task board?"
                    onConfirm={handleDeleteBoard}
                    okText="Yes"
                    cancelText="No"
                    disabled={column.tasks.length > 0}
                >
                    Delete
                </Popconfirm>
            </Menu.Item>
        </Menu>
    );

    return (
        <Col ref={drop} className="p-2 m-2 rounded" style={{
            backgroundColor: '#D3D3D3',
            minWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
        }}>
            <h4 className="mb-2" style={{ color: column.color }}>{column.name}</h4>
            <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                <Dropdown overlay={menu} trigger={['click']}>
                    <EllipsisOutlined style={{ fontSize: '20px', cursor: 'pointer', transform: 'rotate(90deg)' }} />
                </Dropdown>
            </div>

            {column.tasks.map((task: any) => (
                <TaskComponent key={task.TaskID} task={task} moveTask={moveTask} />
            ))}
        </Col>
    );
};

const KanbanBoard: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedPriority, setSelectedPriority] = useState("All");
    const { user } = useSelector((state: any) => state.auth);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [modalType, setModalType] = useState<'edit' | 'create' | ''>('');
    const { taskBoards, loading } = useSelector((state: any) => state.project.taskBoard);
    const { tasks } = useSelector((state: any) => state.project.task);
    const [columns, setColumns] = useState<Record<string, ColumnType>>({});
    const { projectsData } = useSelector((state: any) => state.project.projects);
    const filteredActiveProjects = filterByStatus(projectsData, 'active', 'status');

    const { employeesbyUser } = useSelector((state: any) => state.employee);
    const employeeId = employeesbyUser?.employeeId;

    const activeProjects = filteredActiveProjects?.filter((project: any) => {
        if (!employeeId) return false;

        return (
            project.employee_ids?.includes(employeeId) ||
            project.team_lead_employee_id === employeeId ||
            project.project_manager_employee_id === employeeId
        );
    });

    const [selectedProject, setSelectedProject] = useState<string>('');



    // Permission
    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

    const canAdd = permissions.includes("Add") || permissions.includes("AllowAll");

    useEffect(() => {
        dispatch(fetchAllProjects())
    }, [dispatch])
    useEffect(() => {
        if (!employeesbyUser?.employeeId) {
            dispatch(getEmployeeByUserId({ userId: user?.UserId }));
        }
    })

    useEffect(() => {
        if (!selectedProject && activeProjects.length > 0) {
            setSelectedProject(activeProjects[0].project_id.toString());
        }
    }, [activeProjects, selectedProject]);


    useEffect(() => {
        const updatedColumns = taskBoards
            .filter((board: any) => !selectedProject || board.Project === Number(selectedProject)) // Filter boards by project
            .reduce((columns: any, board: any) => {
                const filteredTasks = tasks.filter((task: any) => {
                    const matchesProject = !selectedProject || Number(task?.Project) === Number(selectedProject);
                    const matchesStatus =
                        task?.Status?.trim().toLowerCase() === board?.Name?.trim().toLowerCase();

                    const matchesPriority =
                        selectedPriority === "All" ||
                        task?.Priority?.toLowerCase() === selectedPriority?.toLowerCase();

                    return matchesProject && matchesStatus && matchesPriority;
                });

                columns[board?.Name?.toLowerCase().replace(/\s+/g, '')] = {
                    name: board.Name,
                    color: board.Color,
                    tasks: filteredTasks,
                    taskBoardId: board.TaskBoardID,
                };
                return columns;
            }, {});

        setColumns(updatedColumns);
    }, [selectedPriority, tasks, taskBoards, selectedProject]);

    const [taskBoardFormData, setTaskBoardFormData] = useState({
        Name: '',
        Project: '',
        Description: '',
        Color: '#dc3545',
        Status: 'active',
    });
    const [errors, setErrors] = useState({
        Name: '',
        Project: '',
        Description: '',
    });

    const resetForm = () => {
        setTaskBoardFormData({
            Name: '',
            Project: '',
            Description: '',
            Color: '#dc3545',
            Status: 'active',
        });
        setErrors({
            Name: '',
            Project: '',
            Description: '',
        });
    };
    const validateForm = () => {
        const newErrors: any = {
            Name: '',
            Project: '',
            Description: '',
            Status: '',
        };
        let isValid = true;

        if (!taskBoardFormData.Name.trim()) {
            newErrors.Name = 'Board name is required.';
            isValid = false;
        }
        if (!taskBoardFormData.Project.trim()) {
            newErrors.Project = 'Project is required.';
            isValid = false;
        }
        setErrors(newErrors);
        return isValid;
    };

    const showModal = (type: 'edit' | 'create') => {
        setModalType(type);
        setIsModalVisible(true);
    };
    const handleCancel = () => {
        setIsModalVisible(false);
        setModalType('');
        resetForm();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) {
            setTaskBoardFormData((prevState) => ({
                ...prevState,
                [name]: value,
            }));
        }
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: '',
        }));
    };

    const handleColorSelection = (Color: string) => {
        setTaskBoardFormData((prevState) => ({
            ...prevState,
            Color: Color,
        }));
    };
    const handleSubmit = async () => {
        const isValid = validateForm();

        if (!isValid) {
            return;
        }
        const updatedFormData = {
            ...taskBoardFormData,
            CreatedBy: modalType === 'create' ? user.UserId : undefined,
            ModifiedBy: modalType === 'edit' ? user.UserId : undefined,
        };
        try {
            let result;
            if (modalType === 'create') {
                result = await dispatch(createTaskBoard(updatedFormData)).unwrap();
            } else if (modalType === 'edit') {
                result = await dispatch(updateTaskBoard({ projectId: user.UserId, ...updatedFormData })).unwrap();
            }
            if (result) {
                message.success(result.message);
                await dispatch(fetchTaskBoards())
                resetForm();
            } else {
                alert(result?.payload.error);
            }
            handleCancel();
        } catch (error: any) {
            message.error(error?.error || "An Error Occurred");
        }
    };

    useEffect(() => {
        dispatch(fetchTaskBoards());
        dispatch(fetchTasks());
    }, [dispatch])

    const moveTask = (taskId: number, targetColumnId: string) => {
        setColumns((prev: any) => {
            const newColumns = JSON.parse(JSON.stringify(prev));

            const sourceColumnId = Object.keys(newColumns).find((colId) =>
                newColumns[colId].tasks.some((task: any) => task.TaskID === taskId)
            );

            if (!sourceColumnId || sourceColumnId === targetColumnId) return prev;

            const taskIndex = newColumns[sourceColumnId].tasks.findIndex(
                (task: any) => task.TaskID === taskId
            );
            if (taskIndex === -1) return prev;

            const [taskToMove] = newColumns[sourceColumnId].tasks.splice(taskIndex, 1);
            newColumns[targetColumnId].tasks.push(taskToMove);

            const updatedData = {
                Status: targetColumnId,
            };
            dispatch(updateTask({ taskId, ...updatedData }));
            return { ...newColumns };
        });
    };

    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Task Board</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to="index.html">
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Projects</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Task Board
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            {canAdd && (
                                <button
                                    onClick={() => showModal('create')}
                                    className="btn btn-primary d-inline-flex align-items-center"
                                >
                                    <i className="ti ti-circle-plus me-1" />
                                    Add Board
                                </button>
                            )}
                            <div className="head-icons ms-2 mb-0">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="d-flex align-items-center flex-wrap mb-3">
                                        {/* <h6 className="me-2">Priority</h6> */}
                                        <ul
                                            className="nav nav-pills border d-inline-flex p-1 rounded bg-light todo-tabs"
                                            id="pills-tab"
                                            role="tablist"
                                        >
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto ${selectedPriority === "All" ? "active" : ""}`}
                                                    onClick={() => setSelectedPriority("All")}
                                                    type="button"
                                                    role="tab"
                                                >
                                                    All
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto ${selectedPriority === "High" ? "active" : ""}`}
                                                    onClick={() => setSelectedPriority("High")}
                                                    type="button"
                                                    role="tab"
                                                >
                                                    High
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto ${selectedPriority === "Medium" ? "active" : ""}`}
                                                    onClick={() => setSelectedPriority("Medium")}
                                                    type="button"
                                                    role="tab"
                                                >
                                                    Medium
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className={`nav-link btn btn-sm btn-icon py-3 d-flex align-items-center justify-content-center w-auto ${selectedPriority === "Low" ? "active" : ""}`}
                                                    onClick={() => setSelectedPriority("Low")}
                                                    type="button"
                                                    role="tab"
                                                >
                                                    Low
                                                </button>
                                            </li>
                                        </ul>

                                        {/* Add a new wrapper here for select input */}
                                        <div className="ms-auto">
                                            <select
                                                className="form-select"
                                                style={{ minWidth: '200px' }}
                                                value={selectedProject}
                                                onChange={(e) => setSelectedProject(e.target.value)}
                                            >
                                                {activeProjects.map((project: any) => (
                                                    <option key={project.project_id} value={project.project_id}>
                                                        {project.project_name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <DndProvider backend={HTML5Backend}>
                                <Container>
                                    <Row className="d-flex justify-content-start flex-nowrap"
                                        style={{ overflowX: 'auto' }}>
                                        {Object.entries(columns).map(([id, column]) => (
                                            <Column key={id} id={id} column={column} moveTask={moveTask} taskBoardId={column.taskBoardId} />
                                        ))}
                                    </Row>
                                </Container>
                            </DndProvider>
                        </div>
                    </div>
                </div>


                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{" "}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
            {/* Add Board */}
            <Drawer
                title={modalType === "edit" ? "Edit Task board" : "Add Task Board"}
                placement="right"
                width={400}
                onClose={handleCancel}
                visible={isModalVisible}
                style={{ marginTop: 50 }}
            >
                <form onSubmit={(e) => e.preventDefault()}>
                    <div className="modal-body">
                        {/* Board Name */}
                        <div className="mb-2">
                            <label className="form-label">Board Name <span className="text-danger"> *</span></label>
                            <input
                                type="text"
                                className="form-control"
                                name="Name"
                                value={taskBoardFormData.Name}
                                onChange={handleInputChange}
                            />
                            {errors.Name && <div className="text-danger">{errors.Name}</div>}
                        </div>
                        <div className="mb-2 ">
                            <label className="form-label">Project <span className="text-danger"> *</span></label>
                            <select
                                className="form-control"
                                name="Project"
                                value={taskBoardFormData.Project}
                                onChange={handleInputChange}
                            >
                                <option value="">Select</option>
                                {activeProjects
                                    .sort((a, b) => a.project_name.localeCompare(b.project_name))
                                    .map((project) => (
                                        <option key={project.project_id} value={project.project_id}>
                                            {project.project_name}
                                        </option>
                                    ))}
                            </select>
                            {errors.Project && <div className="text-danger">{errors.Project}</div>}
                        </div>

                        {/* <div className="mb-2 ">
                            <label className="form-label">Status <span className="text-danger"> *</span></label>
                            <select
                                className="form-control"
                                name="Status"
                                value={taskBoardFormData.Status}
                                onChange={handleInputChange}
                            >
                                {status.map((status) => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                            {errors.Status && <div className="text-danger">{errors.Status}</div>}
                        </div> */}
                        {/* Description */}
                        <div className="mb-2">
                            <label className="form-label">Description </label>
                            <input
                                type="text"
                                className="form-control"
                                name="Description"
                                value={taskBoardFormData.Description}
                                onChange={handleInputChange}
                            />
                            {errors.Description && <div className="text-danger">{errors.Description}</div>}
                        </div>
                        {/* Board Color */}
                        <label className="form-label">Board Color</label>
                        <div className="d-flex align-items-center flex-wrap row-gap-3">
                            <div className="theme-colors mb-4">
                                <ul className="d-flex align-items-center">
                                    {/* Define Color options with onClick handler */}
                                    {['#007bff', '#6c757d', '#17a2b8', '#6f42c1', '#e83e8c', '#ffc107', '#dc3545'].map((Color) => (
                                        <li key={Color}>
                                            <span
                                                className="themecolorset"
                                                onClick={() => handleColorSelection(Color)}
                                            >
                                                <span
                                                    className="primecolor"
                                                    style={{
                                                        backgroundColor: Color,
                                                        border: taskBoardFormData.Color === Color ? '3px solid #fff' : 'none',
                                                        boxShadow: taskBoardFormData.Color === Color ? '0 0 10px rgba(0, 0, 0, 0.2)' : 'none',
                                                    }}
                                                >
                                                    {taskBoardFormData.Color === Color && (
                                                        <span className="colorcheck text-white">
                                                            <i className="ti ti-check text-primary fs-10" />
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light me-2"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                            {loading && <span className="spinner-border spinner-border-sm me-2" />}
                            {modalType === 'edit' ? 'Update Board' : 'Add Board'}
                        </button>
                    </div>
                </form>
            </Drawer >
            {/* /Add Board */}
        </>

    );
};

export default KanbanBoard;
